'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import { Icon } from '@iconify/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCounts, useNotifications } from '@novu/react';
import { useAuth } from '@/infrastructure/auth';
import { isNovuEnabled } from '@/infrastructure/notifications/novu-config';
import { ICONS } from '@/shared/constants';
import { formatTimeAgo } from '@/shared/utils';

/**
 * Auth-safe wrapper: only mounts the inner bell when the NovuProvider
 * is guaranteed to be present (authenticated, and Novu configured).
 */
export function NotificationBell() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated || !isNovuEnabled()) return null;
  return <NotificationBellInner />;
}

function NotificationBellInner() {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { counts, refetch: refetchCounts } = useCounts({ filters: [{ read: false }] });
  const unreadCount = counts?.[0]?.count ?? 0;

  const {
    notifications = [],
    isLoading,
    isFetching,
    hasMore,
    fetchMore,
    refetch,
    readAll,
  } = useNotifications();

  // Poll unread counts as a fallback for the Novu SDK WebSocket bug:
  // When notifications have tags, the SDK's `areTagsEqual` check fails
  // against our tagless `{ read: false }` filter, causing it to skip
  // the count refetch on `notification_received` events entirely.
  useEffect(() => {
    const interval = setInterval(() => refetchCounts(), 60_000);
    return () => clearInterval(interval);
  }, [refetchCounts]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    refetch();
    refetchCounts();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: NonNullable<typeof notifications>[number]) => {
    notification.read();
    setTimeout(() => refetchCounts(), 300);

    const ctaUrl =
      notification.redirect?.url ??
      (notification.data as Record<string, unknown> | undefined)?.ctaUrl;

    if (typeof ctaUrl === 'string') {
      const targetPath = ctaUrl.startsWith('http')
        ? new URL(ctaUrl).pathname
        : ctaUrl;

      // Invalidate relevant caches so the page shows fresh data
      queryClient.invalidateQueries({ queryKey: ['translations'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      if (targetPath === pathname) {
        router.refresh();
      } else {
        router.push(targetPath);
      }
    }

    handleClose();
  };

  const handleMarkAllRead = () => {
    readAll();
    // Novu SDK doesn't always sync counts after readAll — force refetch
    setTimeout(() => refetchCounts(), 300);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          aria-label="notifications"
          aria-controls={open ? 'notifications-popover' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleOpen}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
          >
            <Icon
              icon={unreadCount ? ICONS.NOTIFICATIONS_ACTIVE : ICONS.NOTIFICATIONS}
              width={24}
              height={24}
            />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id="notifications-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 380,
              maxHeight: 480,
              mt: 1,
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<Icon icon={ICONS.MARK_READ} width={16} height={16} />}
              onClick={handleMarkAllRead}
              sx={{ textTransform: 'none' }}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {/* Notification List */}
        <List
          disablePadding
          sx={{
            overflowY: 'auto',
            flexGrow: 1,
          }}
        >
          {(isLoading || (isFetching && notifications.length === 0)) && (
            Array.from({ length: 3 }).map((_, i) => (
              <ListItem key={i} sx={{ px: 2, py: 1.5 }}>
                <ListItemText
                  primary={<Skeleton width="60%" />}
                  secondary={<Skeleton width="90%" />}
                />
              </ListItem>
            ))
          )}

          {!isLoading && !isFetching && notifications.length === 0 && (
            <Box
              sx={{
                py: 6,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon icon={ICONS.NOTIFICATIONS} width={40} height={40} color="var(--color-on-surface-variant)" />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No notifications yet
              </Typography>
            </Box>
          )}

          {notifications.map((notification) => (
            <ListItemButton
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                '&:hover': { backgroundColor: 'action.selected' },
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    fontWeight={notification.isRead ? 400 : 600}
                    noWrap
                  >
                    {notification.subject ?? 'Notification'}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {notification.body}
                    </Typography>
                    <Typography component="span" variant="caption" color="primary.main" sx={{ mt: 0.5, display: 'block', fontWeight: 500 }}>
                      {notification.createdAt ? formatTimeAgo(notification.createdAt) : ''}
                    </Typography>
                  </>
                }
              />
              {!notification.isRead && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    flexShrink: 0,
                    ml: 1,
                  }}
                />
              )}
            </ListItemButton>
          ))}

          {hasMore && (
            <>
              <Divider />
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => fetchMore()}
                  sx={{ justifyContent: 'center', py: 1.5 }}
                >
                  <Typography variant="body2" color="primary">
                    Load more
                  </Typography>
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Popover>
    </>
  );
}
